import React, {useRef, useState} from "react";
import {useGuideFlowProviderContext} from "../../../GuideFlowProviderContext";
import {useForm} from "react-hook-form";
import {useNavigate} from "react-router-dom";
import toast from "react-hot-toast";
import {Button} from "../../controls/tailwindui/button";
import SpinnerIcon from "../../controls/icons/SpinnerIcon";
import {SECTION_ROOT} from "./FormTypes";
import {JournalContent, JournalEntry} from "../../../providers/journal/JournalProvider";
import {RichTextEditor, RichTextEditorRef} from "../../controls/vidence/richtext/RichTextEditor";
import {PencilSquareIcon} from "@heroicons/react/24/outline";
import {stripHtml} from "string-strip-html";

const emptyEntry: JournalEntry = {
    id: "",
    title: "",
};

export default function AddNewJournalEntryView() {
    const navigate = useNavigate();
    const context = useGuideFlowProviderContext();
    const {reset, handleSubmit,getValues} = useForm<JournalEntry>({defaultValues: emptyEntry});
    const editorRef = useRef<RichTextEditorRef>(null);
    const entryIdRef = useRef<string>("");
    
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [lastSavedContent, setLastSavedContent] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [, setGeneratedTitle] = useState('');

    const save = async (entry: JournalEntry, editorContent: any) => {
        console.info("Saving", entry);
        const content: JournalContent = {
            jsonContent: JSON.stringify(editorContent)
        };
        
        if(!editorContent)
        {
            alert('Hold up there, the editor content is empty... aborting save')
            return;
        }
        
        entry.id = entryIdRef.current;
        entryIdRef.current = await context.Journal.save(entry, content)
        context.Journal.get(entryIdRef.current).then(j => reset(j));
    };

    const onSubmit = async (entry: JournalEntry) => {
        // const editorContent = editorRef.current?.getContent();

        // Safely convert to string
        const rawContent = editorRef.current?.getContent();
        const editorContent = typeof rawContent === "string" ? rawContent : JSON.stringify(rawContent);

        const isEmpty = !editorContent || stripHtml(editorContent).result.trim() === "";

        if (isEmpty) {
            toast.error("You should probably write something...");
            return;
        }

        entry.title = editorRef.current?.getTitle?.() ?? "";
        
        setIsSaving(true)
        await save(entry, editorContent);
        // reset(emptyEntry);
        toast.success("New entry added", {icon: '🥰'});
        setIsSaving(false);
        navigate(SECTION_ROOT);
    };

    const titleText = editorRef.current?.getTitle?.() === "" ?
        "Type to create your title..." :
        editorRef.current?.getTitle?.();

    return (
        <div className="p-3">
            <div>
                <h2 className="text-2xl font-bold tracking-tight text-gray-900">New Journal</h2>
                <p className="mt-1 text-sm leading-6 text-gray-400">
                    Compose your entry.
                </p>

                <p className="mt-1 text-xs leading-6 text-gray-400">
                    If you're looking for a date, it's not there. Your job is to keep up with your journal entries;
                    daily.
                    Is that severe? ⚔️
                </p>
                <p className="mt-1 text-xs leading-6 text-gray-400 mb-3">
                    Probably; get over it - I love you anyway.
                </p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="xl:gap-x-16">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-gray-500">
                            {titleText}
                        </p>
                        {isAutoSaving && (
                            <PencilSquareIcon className="h-4 w-4 text-gray-400" />
                        )}
                    </div>

                    <div className="relative border rounded-md p-4 bg-white shadow-sm">
                        <RichTextEditor
                            onTitleChange={setGeneratedTitle}
                            ref={editorRef}
                            onShouldAutoSave={async () => {

                                const editorContent = editorRef.current?.getContent();
                                const content: JournalContent = {
                                    jsonContent: JSON.stringify(editorContent)
                                };

                                if (content.jsonContent === lastSavedContent) {
                                    return;
                                }

                                setIsAutoSaving(true);
                                try {
                                    const entry = getValues();
                                    entry.title =  editorRef.current?.getTitle?.() ?? "";
                                    await save(entry, editorContent);
                                    reset(entry);
                                    setLastSavedContent(content.jsonContent);
                                    
                                } catch (e) {
                                    console.error("Auto-save failed", e);
                                } finally {
                                    setIsAutoSaving(false);
                                }
                            }}
                        />
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-6">
                    <Button type="button" disabled={isSaving} onClick={() => navigate(SECTION_ROOT)} outline={true}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSaving}
                        color="indigo">
                        {isSaving && <SpinnerIcon/>}
                        Save
                    </Button>
                </div>
            </form>
        </div>
    )
}
